import { API_BASE_URL } from "src/config/apiBase";
import React, { useEffect, useState } from "react";
import Select from "react-select";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { toast } from "react-toastify";
import { decryptToken } from "../utils/getToken";
import ReminderPicker from "../../shared/tasks/components/ReminderPicker";
import RepeatAdvancedFields from "../../shared/tasks/components/RepeatAdvancedFields";
import {
    appendReminderFields,
    reminderPresetFromTask,
} from "../../shared/tasks/utils/taskReminderSync";

const TaskModalEdit = ({ isOpen, setIsOpen, leadOptions, assignOptions, taskEdit, fetchTask }) => {
    const [dueDate, setDueDate] = useState();
    const [startDate, setStartDate] = useState();
    const [repetition, setRepetition] = useState(null);
    const [startTime, setStartTime] = useState();
    const [customDate, setCustomDate] = useState(null);
    const [title, setTitle] = useState("");
    const [priority, setPriority] = useState(null);
    const [list, setList] = useState(null);
    const [notes, setNotes] = useState("");
    const [file, setFile] = useState(null);
    const [reminderPreset, setReminderPreset] = useState("none");
    const [reminderCustomMinutes, setReminderCustomMinutes] = useState(15);
    const [repeatInterval, setRepeatInterval] = useState(1);
    const [repeatDays, setRepeatDays] = useState([]);
    const [repeatEndType, setRepeatEndType] = useState("never");
    const [repeatEndDate, setRepeatEndDate] = useState("");

    const url = API_BASE_URL;
    const token = decryptToken("accessToken") || "";

    const repetitionOptions = [
        { value: "none", label: "Does not repeat" },
        { value: "daily", label: "Daily" },
        { value: "weekdays", label: "Weekdays" },
        { value: "weekly", label: "Weekly" },
        { value: "monthly", label: "Monthly" },
        { value: "annually", label: "Yearly" },
        { value: "custom", label: "Custom" },
    ];

    const priorityOptions = [
        { value: "high", label: "High" },
        { value: "medium", label: "Medium" },
        { value: "low", label: "Low" },
    ];

    const capitalize = (str) => {
        if (!str) return "";
        return str.charAt(0).toUpperCase() + str.slice(1);
    };

    const parseRepeatDays = (raw) => {
        if (!raw) return [];
        if (Array.isArray(raw)) return raw;
        try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    };

    useEffect(() => {
        if (taskEdit) {
            const safeParseDate = (dateStr) => {
                if (!dateStr || dateStr === "0000-00-00") return null;
                const isoString = dateStr.includes("T") ? dateStr : dateStr.replace(" ", "T");
                const parsed = new Date(isoString);
                return isNaN(parsed) ? null : parsed;
            };
            setTitle(taskEdit.title || "");
            setPriority(
                taskEdit.priority
                    ? { value: taskEdit.priority, label: capitalize(taskEdit.priority) }
                    : null
            );
            setRepetition(
                taskEdit.repetition
                    ? { value: taskEdit.repetition, label: capitalize(taskEdit.repetition) }
                    : null
            );
            setDueDate(safeParseDate(taskEdit.due_date) || new Date());
            setStartDate(safeParseDate(taskEdit.start_date) || new Date());
            setCustomDate(safeParseDate(taskEdit.customDate));
            setStartTime(taskEdit.start_time || "09:00");
            setReminderPreset(reminderPresetFromTask(taskEdit));
            setReminderCustomMinutes(taskEdit.reminder_minutes || 15);
            setRepeatInterval(taskEdit.repeat_interval || 1);
            setRepeatDays(parseRepeatDays(taskEdit.repeat_days));
            setRepeatEndType(taskEdit.repeat_end_type || "never");
            setRepeatEndDate(
                taskEdit.repeat_end_date
                    ? new Date(taskEdit.repeat_end_date).toISOString().slice(0, 10)
                    : ""
            );
            const listObj = leadOptions.find((l) => l.value === taskEdit.list_id);
            setList(listObj || null);
            setNotes(taskEdit.notes || "");
            setFile(null);
        }
    }, [taskEdit, leadOptions]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const formData = new FormData();
            formData.append("title", title);
            formData.append("due_date", dueDate);
            formData.append("priority", priority?.value || "");
            formData.append("repetition", repetition?.value || "none");
            if (repetition?.value === "custom") {
                formData.append("customDate", customDate);
            }
            formData.append("list_id", list?.value || "");
            formData.append("notes", notes);
            formData.append("start_time", startTime || "09:00");
            formData.append("start_date", startDate);
            appendReminderFields(formData, reminderPreset, reminderCustomMinutes);
            if (repeatInterval > 1) formData.append("repeat_interval", String(repeatInterval));
            if (repeatDays.length) formData.append("repeat_days", JSON.stringify(repeatDays));
            if (repeatEndType) formData.append("repeat_end_type", repeatEndType);
            if (repeatEndDate) formData.append("repeat_end_date", repeatEndDate);
            if (file) {
                formData.append("attachment", file);
            }

            const response = await fetch(`${url}/task/stafftask/${taskEdit.id}`, {
                method: "PUT",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: formData,
            });

            const res = await response.json();
            if (response.ok) {
                toast.success("Task updated successfully!");
                fetchTask?.();
                setIsOpen(false);
            } else {
                toast.error(res.error || "Failed to update task");
            }
        } catch (error) {
            toast.error(error.message);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="task-modal-overlay" onClick={() => setIsOpen(false)}>
            <div className="task-modal-panel" onClick={(e) => e.stopPropagation()}>
                <h2 className="oms-modal-title">Edit Task</h2>

                <form className="oms-modal-form" onSubmit={handleSubmit}>
                    <div className="oms-form-group">
                        <label htmlFor="task-edit-title" className="oms-form-label">
                            Title
                        </label>
                        <input
                            id="task-edit-title"
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Enter task title"
                        />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="oms-form-group">
                            <label className="oms-form-label">Start Date</label>
                            <Flatpickr
                                value={startDate}
                                onChange={(date) => setStartDate(date[0])}
                                placeholder="Select Date"
                            />
                        </div>
                        <div className="oms-form-group">
                            <label className="oms-form-label">Due Date</label>
                            <Flatpickr
                                value={dueDate}
                                onChange={(date) => setDueDate(date[0])}
                                placeholder="Select Date"
                            />
                        </div>

                        <div className="oms-form-group">
                            <label className="oms-form-label">Due Time</label>
                            <Flatpickr
                                options={{
                                    enableTime: true,
                                    noCalendar: true,
                                    dateFormat: "H:i",
                                    time_24hr: true,
                                }}
                                value={startTime}
                                onChange={(date, dateStr) => setStartTime(dateStr)}
                                placeholder="Select due time"
                            />
                        </div>

                        <div className="oms-form-group">
                            <label className="oms-form-label">Reminder</label>
                            <ReminderPicker
                                value={reminderPreset}
                                customMinutes={reminderCustomMinutes}
                                onChange={({ preset, customMinutes }) => {
                                    setReminderPreset(preset);
                                    if (customMinutes) setReminderCustomMinutes(customMinutes);
                                }}
                            />
                        </div>

                        <div className="oms-form-group">
                            <label className="oms-form-label">Priority</label>
                            <Select
                                options={priorityOptions}
                                value={priority}
                                onChange={setPriority}
                                placeholder="Select priority"
                            />
                        </div>

                        <div className="oms-form-group sm:col-span-2">
                            <label className="oms-form-label">Repetition</label>
                            <Select
                                options={repetitionOptions}
                                value={repetition}
                                onChange={setRepetition}
                                placeholder="Select repetition"
                            />
                            {repetition?.value === "custom" && (
                                <Flatpickr
                                    value={customDate}
                                    onChange={(date) => setCustomDate(date[0])}
                                    placeholder="Pick custom date"
                                />
                            )}
                            {repetition?.value && repetition.value !== "none" && (
                                <RepeatAdvancedFields
                                    repetition={repetition.value}
                                    repeatInterval={repeatInterval}
                                    repeatDays={repeatDays}
                                    repeatEndType={repeatEndType}
                                    repeatEndDate={repeatEndDate}
                                    onChange={(patch) => {
                                        if (patch.repeat_interval != null) setRepeatInterval(patch.repeat_interval);
                                        if (patch.repeat_days) setRepeatDays(patch.repeat_days);
                                        if (patch.repeat_end_type) setRepeatEndType(patch.repeat_end_type);
                                        if (patch.repeat_end_date != null) setRepeatEndDate(patch.repeat_end_date);
                                    }}
                                />
                            )}
                        </div>

                        <div className="oms-form-group">
                            <label className="oms-form-label">Add to List</label>
                            <Select
                                options={leadOptions}
                                value={list}
                                onChange={setList}
                                placeholder="Select list"
                                getOptionLabel={(option) => (
                                    <div className="flex items-center">
                                        <span
                                            className="inline-block w-3 h-3 rounded-full mr-2 shrink-0"
                                            style={{ backgroundColor: option.color }}
                                        />
                                        {option.label}
                                    </div>
                                )}
                                getOptionValue={(option) => option.value}
                            />
                        </div>
                    </div>

                    <div className="oms-form-group">
                        <label className="oms-form-label">Notes</label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Enter notes"
                            rows={3}
                        />
                    </div>

                    <div className="oms-form-group">
                        <label className="oms-form-label">Attachment</label>
                        <input type="file" onChange={(e) => setFile(e.target.files[0])} />
                        {taskEdit?.attachment && (
                            <img
                                className="task-modal-attachment"
                                src={`${url}/${taskEdit.attachment.replace(/\\/g, "/")}`}
                                alt="Current attachment"
                            />
                        )}
                    </div>

                    <div className="task-modal-actions">
                        <button
                            type="button"
                            onClick={() => setIsOpen(false)}
                            className="task-modal-btn task-modal-btn--ghost"
                        >
                            Cancel
                        </button>
                        <button type="submit" className="task-modal-btn task-modal-btn--primary">
                            Save Task
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default TaskModalEdit;
